import { DivisionQuery, Dto, Query } from '@damofire/data-commons';
import { Description } from '../des.dto';
import { IssueUserType, RiskLevel } from '../issue/issue.dto';
import { Noteable } from '../note/note.dto';

export interface Asset extends Noteable {
  section?: string;
  assetId?: number;
  assetType?: Description<number>;
  clientCode?: string;
  description?: string;
  system?: number;
  upright?: number;
  riskLevel?: RiskLevel;
  warehouseId?: number;
  statistics?: AssetStatistics;
  nextAsset?: number;
  previousAsset?: number;
  toRemove?: boolean;
  aisle?: string;
}

export type EmbeddedAsset = { _id?: number | string } & Pick<Asset, 'description' | 'system' | 'upright' | 'clientCode' | 'section'> & { aisle?: string };

// TODO: use _id instead of description and update peen-asset-types collection
export enum AssetTypeDescEnum {
  PALLET_RACK = 'PALLET_RACK',
  CANTILEVER = 'CANTILEVER',
}

export enum AssetTypeId {
  PALLET_RACK = 1,
  CANTILEVER = 2,
}

export interface AssetStatistics {
  issues?: IssuesStatistics;
}

export interface IssuesStatistics {
  issueSequenceNumber?: number;
  unresolved?: number;
  resolved?: number;
  total?: number;
  createdByType?: { [key in IssueUserType]: number };
}

export interface InspectionsStatistics {
  inProgress?: number;
  completed?: number;
  total?: number;
  damo?: number;
  self?: number;
}

export interface System2d extends Dto<string> {
  depth?: number;
  divisionId?: string;
  number?: number;
  aisle?: string;
  organizationId?: string;
  systemId?: string;
  xDirection?: number;
  xOrigin?: number;
  yDirection?: number;
  yOrigin?: number;
  uprights?: Upright[];
}

export interface IdbSystem2d extends System2d {
  front?: string;
  frontPath?: string;
  division?: Dto<string>;
}

export interface System {
  first?: Asset;
  last?: Asset;
}

export interface Bay {
  leftAssetId?: string;
  rightAssetId?: string;
}

export interface Upright2d extends Upright {
  columns?: Column[];
}

export interface Upright extends Dto<string> {
  assetTypeId?: number;
  assetId?: string;
  systemId?: string;
  divisionId?: string;
  organizationId?: string;
  capacity?: number;
  x?: number;
  y?: number;
  index?: number;
  beamLength?: number;
  orientation?: 'left' | 'right';
  section?: string;
  clientCode?: string;
}

export interface IdbUpright extends Upright {
  division?: Dto<string>;
}

export interface AssetQuery extends Query {
  filters: { 'asset._id': number; [key: string]: string | number };
}

export interface AssetTypeQuery extends DivisionQuery {
  filters: { 'assetType._id': number } & DivisionQuery['filters'];
}

export interface Column extends Dto<string> {
  assetId?: string;
  baseplateDepth?: number;
  baseplateThickness?: number;
  baseplateWidth?: number;
  col1Id?: string;
  col2Id?: string;
  col3Id?: string;
  col1Height?: number;
  col2Height?: number;
  col3Height?: number;
  panelDepth?: number;
  rowIndex?: number;
  s?: number;
  columnProperties?: Record<string, ColumnProperties>;
  beamProperties?: Record<string, BeamProperties>;
  division?: Dto<string>;
  palletLevels?: PalletLevel[];
  panels?: ColumnPanel[];
}

export const PALLET_LEVEL_USAGE_PALLET = 'Pallet';
export const PALLET_LEVEL_USAGE_PICKING = 'Picking';
export const PALLET_LEVEL_USAGE_NOT_USED = 'Not Used';
export type PalletLevelUsage = 'Picking' | 'Pallet' | 'Not Used';

export interface PalletLevel extends Dto<string> {
  beamDepth?: number;
  beamHeight?: number;
  beamId?: string;
  beamType?: string;
  columnLeftId?: string;
  elevation?: number;
  maxLoad?: number;
  palletCount?: number;
  palletWeight?: number;
  usage?: PalletLevelUsage;
  division?: Dto<string>;
}

export interface ColumnPanel extends Dto<string> {
  bracePattern?: string;
  columnId?: string;
  elevation?: number;
  division?: Dto<string>;
}

export interface ColumnProperties extends Dto<string> {
  depth?: number;
  drawingNumber?: string;
  effectiveArea?: number;
  elasticModulus?: number;
  flangeLength?: number;
  grossArea?: number;
  gyrationRadiusX?: number;
  gyrationRadiusY?: number;
  manufactureName?: string;
  name?: string;
  netArea?: number;
  plateThickness?: number;
  rationalGyrationRadius?: number;
  shearCenterToCentroid?: number;
  shearModulus?: number;
  torsionalFactor?: number;
  type?: string;
  ultimateStress?: number;
  warpingFactor?: number;
  yieldStress?: number;
}

export interface BeamProperties extends Dto<string> {
  name?: string;
  capacityPerBeamPair?: number;
  compatibleHoleType?: string;
  depth?: number;
  gauge?: number;
  height?: number;
  manufacturerName?: string;
  notes?: string;
  span?: number;
  stepDepth?: number;
  stepHeight?: number;
  type?: string;
  webThickness?: number;
  drawingNumber?: string;
}
